import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { useLanguage, api } from '../context/AppContext';
import { getWeekIdToronto, getRecentWeekIdsToronto } from '../utils/dateUtils';

/**
 * Compact tracker showing the user's progress on their 7-Day Battle Plan.
 *
 * Iter. 406.fr.24 — Single source of truth (production bug fix).
 *   The tracker now reads `completed_days` from `/weekly-quest/current`
 *   (the SERVER-side count that powers the big WeeklyQuestCard), not from
 *   localStorage. Before this fix, the small widget displayed 0/7 while
 *   the detailed quest card already showed 4/7 because they consumed two
 *   different sources. The mission-report's `weekly_battle_plan` is still
 *   read for the "Renforce: <weakest principle>" hint, but is no longer
 *   the count source.
 *
 * - Server count for the current ISO-week → instant sync with WeeklyQuestCard
 * - Tap → navigate to /monthly-report
 * - Hidden when no mission report exists yet (preserves the prior trigger)
 * - Streak rail: best-effort historical view from localStorage (current week
 *   is always accurate thanks to the server feed; past weeks fall back to
 *   the legacy summary key)
 */
const BattlePlanTracker = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (fr, en) => (language === 'fr' ? fr : en);

  const [report, setReport] = useState(null);
  const [quest, setQuest] = useState(null);
  const [checkedCount, setCheckedCount] = useState(0);
  const [tick, setTick] = useState(0); // forces refetch on window focus
  const prevCountRef = useRef(0); // for celebration trigger detection

  // Fetch BOTH endpoints in parallel. The mission report is still used for
  // the "Renforce" hint + visibility gate. The weekly quest is the count
  // source of truth.
  // Iter. 411.mm16 — MERGE-SAFE quest refresh: if a day was completed
  // locally (either by the ambient toggle in <WeeklyQuestCard> or by a
  // prior fetch), never downgrade it back to incomplete when a subsequent
  // GET hits an unreplicated Mongo secondary. Root cause of the "case
  // se décoche après 3s" prod bug: the focus/visibilitychange listeners
  // and the sibling's safety-net refetch overwrote the UI with stale data.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get('/reports/mission').catch(() => ({ data: null })),
      api.get('/weekly-quest/current').catch(() => ({ data: null })),
    ]).then(([reportRes, questRes]) => {
      if (cancelled) return;
      setReport(reportRes?.data?.report || null);
      const server = questRes?.data;
      if (!server || !Array.isArray(server.days)) {
        setQuest(server || null);
        return;
      }
      setQuest((prev) => {
        if (!prev || !Array.isArray(prev.days)) return server;
        const prevByIdx = new Map(prev.days.map((d) => [d.day_index, d]));
        const mergedDays = server.days.map((sd) => {
          const local = prevByIdx.get(sd.day_index);
          if (local?.completed && !sd.completed) {
            return { ...sd, completed: true, completed_at: local.completed_at || sd.completed_at };
          }
          return sd;
        });
        const c = mergedDays.filter((d) => d.completed).length;
        return {
          ...server,
          days: mergedDays,
          completed_days: Math.max(server.completed_days || 0, prev.completed_days || 0, c),
          total_points_awarded: Math.max(
            server.total_points_awarded || 0,
            prev.total_points_awarded || 0,
          ),
        };
      });
    });
    return () => { cancelled = true; };
  }, [tick]);

  // Re-sync count whenever the quest payload changes and trigger the
  // 7/7 celebration on the transition <7 → =7.
  useEffect(() => {
    if (!quest || !Array.isArray(quest.days)) return;
    const total = 7;
    const c = typeof quest.completed_days === 'number'
      ? quest.completed_days
      : quest.days.filter((d) => d.completed).length;
    const prev = prevCountRef.current;
    const weekId = quest.week_id || getWeekIdToronto();
    if (prev < total && c >= total) {
      const celebKey = `yumix_battle_plan_celebrated_${weekId}`;
      let alreadyCelebrated = false;
      try { alreadyCelebrated = localStorage.getItem(celebKey) === '1'; } catch { /* */ }
      if (!alreadyCelebrated) {
        try { localStorage.setItem(celebKey, '1'); } catch { /* */ }
        try {
          confetti({
            particleCount: 140,
            spread: 90,
            startVelocity: 38,
            origin: { x: 0.5, y: 0.3 },
            colors: ['#10B981', '#34D399', '#FBBF24', '#F59E0B', '#FFFFFF'],
            scalar: 1.15,
            ticks: 260,
          });
        } catch { /* canvas-confetti non-critical */ }
        toast.custom((tId) => (
          <div
            className="bg-gradient-to-br from-emerald-500 via-emerald-400 to-amber-300 text-black rounded-2xl px-4 py-3 shadow-2xl border-2 border-emerald-200 max-w-sm flex items-start gap-3 animate-in zoom-in-95 fade-in slide-in-from-top-4 duration-300"
            data-testid="battle-plan-celebration-toast"
            style={{ boxShadow: '0 0 40px rgba(16,185,129,0.55), 0 0 80px rgba(251,191,36,0.30)' }}
            onClick={() => toast.dismiss(tId)}
          >
            <div className="text-3xl animate-bounce" aria-hidden="true">🏆</div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-base uppercase tracking-tight font-black leading-tight">
                {t("Plan d'Attaque maitrise !", 'Battle Plan mastered!')}
              </p>
              <p className="text-xs font-semibold opacity-90 mt-0.5">
                {t(
                  `7/7 cette semaine. ${report?.weakest_principle?.name || ''} renforce.`,
                  `7/7 this week. ${report?.weakest_principle?.name || ''} reinforced.`,
                )}
              </p>
            </div>
          </div>
        ), { duration: 6000, position: 'top-center' });
      }
    }
    prevCountRef.current = c;
    setCheckedCount(c);
    // Persist the current week's count so the streak rail can render a
    // honest history once the week rolls over.
    try { localStorage.setItem(`yumix_battle_plan_summary_${weekId}`, String(c)); } catch { /* */ }
  }, [quest, report]);

  // Refetch on window focus + document visibilitychange, debounced 300 ms
  // so a burst of events during Android fullscreen-exit collapses into one.
  // Also skip the bump if we fetched less than 10 s ago (the parent fetch
  // effect reads `tick` so bumping it = refetch).
  useEffect(() => {
    let debounce = null;
    let lastBump = 0;
    const COOLDOWN = 10000;
    const schedule = () => {
      const now = Date.now();
      if (now - lastBump < COOLDOWN) return;
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => {
        debounce = null;
        lastBump = Date.now();
        setTick((x) => x + 1);
      }, 300);
    };
    const onFocus = () => schedule();
    const onVisibility = () => { if (document.visibilityState === 'visible') schedule(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (debounce) clearTimeout(debounce);
    };
  }, []);

  // Iter. 411.mm4 — Listen for live weekly-quest updates so this tracker
  // resyncs the very second the user ticks a day on WeeklyQuestCard (same
  // Dashboard, no focus event fires otherwise). If the event payload
  // carries a fresh `quest`, use it directly to avoid a round-trip; else
  // bump `tick` to refetch.
  useEffect(() => {
    const onQuestUpdated = (e) => {
      const fresh = e?.detail?.quest;
      if (fresh && Array.isArray(fresh.days)) {
        // Merge-safe: only accept the event's quest if it has AT LEAST as
        // many completed days as what we hold. If for some reason a stale
        // payload sneaks in (e.g. another card reset), fall back to a full
        // refetch — never regress the local UI.
        setQuest((prev) => {
          if (!prev || !Array.isArray(prev.days)) return fresh;
          const localCount = prev.days.filter((d) => d.completed).length;
          const freshCount = fresh.days.filter((d) => d.completed).length;
          if (freshCount < localCount) return prev;
          return fresh;
        });
      } else {
        setTick((x) => x + 1);
      }
    };
    window.addEventListener('yumix:weekly-quest-updated', onQuestUpdated);
    return () => window.removeEventListener('yumix:weekly-quest-updated', onQuestUpdated);
  }, []);

  // Visibility gate: hide the widget until BOTH the report AND a quest exist
  // (matches the prior behaviour — silent fallback when there's nothing to
  // show, but now also requires a quest so we never render 0/7 with stale
  // localStorage). If a quest exists without a report, still show so the
  // user sees their 7-day defi progress.
  if (!quest || !Array.isArray(quest.days) || quest.days.length === 0) return null;

  const total = 7;
  const pct = Math.round((checkedCount / total) * 100);
  const allDone = checkedCount >= total;
  const weakName = report?.weakest_principle?.name || '';

  // Iter. 406.v+ — Streak Hebdo (4 dernières semaines). Le but : créer un
  // effet "série visuelle" addictif qui pousse à compléter cette semaine
  // pour ne pas casser la séquence (mécanique Duolingo / Snap streaks).
  // Lecture seule depuis localStorage — les anciennes semaines ne sont jamais
  // recalculées (seule la semaine courante est mise à jour à chaque tick).
  const weekIds = getRecentWeekIdsToronto(4);
  const currentWeekId = weekIds[weekIds.length - 1];
  const streak = weekIds.map((wid) => {
    const isCurrent = wid === currentWeekId;
    const raw = isCurrent
      ? checkedCount
      : (() => {
          try { return parseInt(localStorage.getItem(`yumix_battle_plan_summary_${wid}`) || '', 10); }
          catch { return NaN; }
        })();
    return { weekId: wid, count: Number.isFinite(raw) ? raw : null, isCurrent };
  });
  // Compute a consecutive "perfect weeks" streak ending at last completed week.
  // (Doesn't include current unless it's already 7/7.)
  let consecutivePerfect = 0;
  for (let i = streak.length - 1; i >= 0; i--) {
    if (streak[i].count === total) consecutivePerfect += 1;
    else if (streak[i].isCurrent) continue; // skip in-progress current week
    else break;
  }

  return (
    <div className="space-y-2" data-testid="battle-plan-block">
    <button
      onClick={() => navigate('/monthly-report')}
      className={`w-full group flex items-center gap-3 rounded-xl border-2 px-3 py-2.5 transition-all active:scale-[0.99] ${
        allDone
          ? 'border-emerald-400/70 bg-gradient-to-r from-emerald-500/25 via-emerald-500/12 to-emerald-400/8 hover:from-emerald-500/30'
          : 'border-amber-400/70 bg-gradient-to-r from-amber-500/25 via-orange-500/15 to-amber-400/10 hover:from-amber-500/35'
      }`}
      style={{
        boxShadow: allDone
          ? '0 0 24px rgba(16,185,129,0.45), 0 0 6px rgba(52,211,153,0.35), inset 0 0 0 1px rgba(52,211,153,0.18)'
          : '0 0 24px rgba(251,191,36,0.42), 0 0 6px rgba(249,115,22,0.35), inset 0 0 0 1px rgba(251,191,36,0.20)',
      }}
      data-testid="battle-plan-tracker"
    >
      <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
        allDone
          ? 'bg-emerald-500/30 ring-1 ring-emerald-300/40'
          : 'bg-amber-500/30 ring-1 ring-amber-300/50'
      }`}>
        {allDone ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-200" />
        ) : (
          <CalendarDays className="w-5 h-5 text-amber-200" />
        )}
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2 mb-1">
          <p className={`text-[10px] uppercase tracking-widest font-bold ${
            allDone ? 'text-emerald-200' : 'text-amber-200'
          }`}>
            {t("Plan d'Attaque", 'Battle Plan')}
          </p>
          <span
            className={`text-xs font-black tabular-nums ${allDone ? 'text-emerald-100' : 'text-amber-50'}`}
            data-testid="battle-plan-tracker-count"
          >
            {checkedCount}/{total}
          </span>
          {allDone && (
            <span className="text-[9px] uppercase tracking-wider text-emerald-200/90 font-semibold">
              · {t('Semaine completee', 'Week complete')}
            </span>
          )}
        </div>
        {/* Progress bar with glow */}
        <div className="w-full bg-black/40 rounded-full h-1.5 overflow-hidden">
          <div
            className={`h-1.5 rounded-full transition-all duration-500 ${
              allDone
                ? 'bg-gradient-to-r from-emerald-300 via-emerald-400 to-emerald-500'
                : 'bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400'
            }`}
            style={{
              width: `${pct}%`,
              boxShadow: allDone
                ? '0 0 8px rgba(52,211,153,0.7)'
                : '0 0 8px rgba(251,191,36,0.7)',
            }}
            data-testid="battle-plan-tracker-bar"
          />
        </div>
        {weakName && !allDone && (
          <p className="text-[9px] text-amber-100/70 mt-0.5 truncate">
            {t('Renforce :', 'Reinforce:')} <span className="text-amber-200">{weakName}</span>
          </p>
        )}
      </div>
      <ChevronRight className={`w-4 h-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${
        allDone ? 'text-emerald-200' : 'text-amber-200'
      }`} />
    </button>

    {/* 📊 Streak Hebdo — 4 dernières semaines. Effet "série visuelle" qui
        pousse à ne pas casser la séquence. La cellule courante est animée
        et reflète l'état temps-réel via `checkedCount`. */}
    <div
      className="flex items-stretch gap-1.5 px-0.5"
      role="group"
      aria-label={t('Streak hebdomadaire des 4 dernières semaines', 'Weekly streak — last 4 weeks')}
      data-testid="battle-plan-streak-rail"
    >
      {streak.map(({ weekId, count, isCurrent }, idx) => {
        const isPerfect = count === total;
        const isPartial = count !== null && count > 0 && !isPerfect;
        const isMissed = count === 0;
        const isUnknown = count === null;
        const cellLabel = isCurrent
          ? t('Cette sem.', 'This wk.')
          : `S-${streak.length - 1 - idx}`;
        const display = isUnknown ? '—' : `${count}/${total}`;
        return (
          <div
            key={weekId}
            data-testid={`battle-plan-streak-cell-${idx}`}
            data-week-id={weekId}
            title={`${cellLabel} · ${weekId} · ${display}`}
            className={`flex-1 rounded-lg border px-1.5 py-1 flex flex-col items-center justify-center transition-all ${
              isPerfect
                ? 'border-emerald-400/60 bg-emerald-500/15 shadow-[0_0_12px_rgba(16,185,129,0.25)]'
                : isPartial
                  ? 'border-amber-400/50 bg-amber-500/10'
                  : isMissed
                    ? 'border-rose-500/30 bg-rose-500/5'
                    : 'border-white/10 bg-white/[0.03]'
            } ${isCurrent ? 'ring-1 ring-amber-300/60 animate-pulse-slow' : ''}`}
          >
            <span className={`text-[8px] uppercase tracking-widest font-semibold ${
              isCurrent ? 'text-amber-200' : 'text-muted-foreground/70'
            }`}>
              {cellLabel}
            </span>
            <span className={`text-[10px] font-black tabular-nums leading-tight ${
              isPerfect ? 'text-emerald-200'
                : isPartial ? 'text-amber-100'
                : isMissed ? 'text-rose-300/80'
                : 'text-muted-foreground/60'
            }`}>
              {isPerfect ? '✓' : display}
            </span>
          </div>
        );
      })}
    </div>

    {consecutivePerfect >= 2 && (
      <p
        className="text-center text-[10px] font-semibold text-emerald-300/90 tracking-wide"
        data-testid="battle-plan-streak-flame"
      >
        🔥 {t(
          `Série de ${consecutivePerfect} semaine${consecutivePerfect > 1 ? 's' : ''} parfaite${consecutivePerfect > 1 ? 's' : ''} — ne casse pas le rythme !`,
          `${consecutivePerfect}-week perfect streak — don't break the rhythm!`,
        )}
      </p>
    )}
    </div>
  );
};

export default BattlePlanTracker;
